/**
 * components/charts/EquityChart — the reusable time-series chart primitive, built to
 * the dataviz skill: a single-series equity curve, so no legend box (the caption names
 * it), a thin 2px line over a recessive area fill, a recessive grid, one selective
 * direct label on the latest point, and a crosshair + tooltip hover layer (an
 * HTML/SVG chart ships interactive by default). Color follows the window's sign —
 * profit-green when up over the range, loss-red when down — never a rainbow.
 *
 * Pure presentational: it takes points and renders SVG; the data seam (live vs seed)
 * lives in the caller.
 */
import { useMemo, useRef, useState } from 'react';

import { money, num, pct } from '../../lib/format';
import type { EquityPoint } from '../../api/types';

interface EquityChartProps {
  readonly points: readonly EquityPoint[];
  readonly height?: number;
  readonly caption?: string;
}

const VIEW_W = 720;
const PAD = { l: 10, r: 62, t: 16, b: 26 } as const;

export function EquityChart({ points, height = 260, caption }: EquityChartProps): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const geo = useMemo(() => {
    const H = height;
    const plotW = VIEW_W - PAD.l - PAD.r;
    const plotH = H - PAD.t - PAD.b;
    if (points.length < 2) return null;

    const ts = points.map((p) => p.at);
    const eq = points.map((p) => p.equity);
    const minT = Math.min(...ts);
    const maxT = Math.max(...ts);
    const minE = Math.min(...eq);
    const maxE = Math.max(...eq);
    const padE = (maxE - minE) * 0.12 || maxE * 0.02 || 1;
    const y0 = minE - padE;
    const y1 = maxE + padE;

    const x = (t: number): number => PAD.l + ((t - minT) / (maxT - minT || 1)) * plotW;
    const y = (e: number): number => PAD.t + (1 - (e - y0) / (y1 - y0 || 1)) * plotH;

    const xy = points.map((p) => ({ px: x(p.at), py: y(p.equity), ...p }));
    const line = xy.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.px.toFixed(1)} ${p.py.toFixed(1)}`).join(' ');
    const baseY = PAD.t + plotH;
    const area = `${line} L${xy[xy.length - 1]!.px.toFixed(1)} ${baseY} L${xy[0]!.px.toFixed(1)} ${baseY} Z`;

    const gridVals = [y1, (y1 + y0) / 2, y0];
    const grid = gridVals.map((v) => ({ v, gy: y(v) }));

    const up = eq[eq.length - 1]! >= eq[0]!;
    return { H, plotW, plotH, baseY, x, y, xy, line, area, grid, minT, maxT, up };
  }, [points, height]);

  if (!geo) {
    return (
      <div className="chart">
        <div className="state" style={{ minHeight: height }}>
          <span className="muted">Not enough data to plot.</span>
        </div>
      </div>
    );
  }

  const color = geo.up ? 'var(--profit)' : 'var(--loss)';
  const gradId = geo.up ? 'eqUp' : 'eqDown';
  const last = geo.xy[geo.xy.length - 1]!;
  const hovered = hover != null ? geo.xy[hover] : null;

  const onMove = (e: React.PointerEvent<SVGSVGElement>): void => {
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * VIEW_W;
    // nearest sample by x
    let best = 0;
    let bestD = Infinity;
    geo.xy.forEach((p, i) => {
      const d = Math.abs(p.px - svgX);
      if (d < bestD) { bestD = d; best = i; }
    });
    setHover(best);
  };

  // Tooltip pixel position within the (relatively-positioned) wrapper.
  const wrapW = wrapRef.current?.clientWidth ?? VIEW_W;
  const scale = wrapW / VIEW_W;

  return (
    <div className="chart" ref={wrapRef} style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${VIEW_W} ${geo.H}`}
        role="img"
        aria-label={caption ?? 'Equity curve over time'}
        preserveAspectRatio="none"
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* recessive grid + right-side value labels */}
        {geo.grid.map((g, i) => (
          <g key={i}>
            <line
              x1={PAD.l} x2={VIEW_W - PAD.r} y1={g.gy} y2={g.gy}
              stroke="var(--viz-grid)" strokeWidth={1}
            />
            <text
              x={VIEW_W - PAD.r + 6} y={g.gy + 3}
              fontSize={11} fill="var(--text-muted)" fontFamily="var(--font-mono)"
            >
              {num(g.v, 0)}
            </text>
          </g>
        ))}

        {/* area + line */}
        <path d={geo.area} fill={`url(#${gradId})`} />
        <path d={geo.line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

        {/* crosshair on hover */}
        {hovered && (
          <g className="chart__crosshair">
            <line x1={hovered.px} x2={hovered.px} y1={PAD.t} y2={geo.baseY} stroke="var(--viz-axis)" strokeWidth={1} strokeDasharray="3 3" />
            <circle cx={hovered.px} cy={hovered.py} r={4} fill={color} stroke="var(--viz-surface)" strokeWidth={2} />
          </g>
        )}

        {/* selective direct label: the latest point only */}
        <circle cx={last.px} cy={last.py} r={4} fill={color} stroke="var(--viz-surface)" strokeWidth={2} />

        {/* x-axis endpoints */}
        <text x={PAD.l} y={geo.H - 8} fontSize={11} fill="var(--text-muted)">
          {shortTime(geo.minT)}
        </text>
        <text x={VIEW_W - PAD.r} y={geo.H - 8} fontSize={11} fill="var(--text-muted)" textAnchor="end">
          {shortTime(geo.maxT)}
        </text>
      </svg>

      {hovered && (
        <div
          className="chart-tooltip"
          style={{ left: hovered.px * scale, top: hovered.py * scale }}
        >
          <strong className="num">{money(hovered.equity)}</strong>
          <span className="muted" style={{ marginLeft: 8 }}>{fullTime(hovered.at)}</span>
        </div>
      )}

      <div className="chart__caption">
        <span className="chart__legend-swatch" style={{ background: color }} aria-hidden />
        <span>{caption ?? 'Account equity'}</span>
        <span style={{ marginLeft: 'auto' }} className="num">
          {money(last.equity, 0)} · <span className={geo.up ? 'profit' : 'loss'}>{pct((last.equity - geo.xy[0]!.equity) / geo.xy[0]!.equity)}</span>
        </span>
      </div>
    </div>
  );
}

function shortTime(t: number): string {
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
function fullTime(t: number): string {
  return new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
}
